import { useState, useEffect, useMemo } from 'react';
import { FilterToggle, FiltersPanel, SearchableMultiSelect } from '@components/filters';
import { getPublicPlans, getPublicPlanByIdFromAPI, type PublicPlan } from '@data/publicPlans';
import { getOffers, type Offer } from '@data/offers';
import { stylePresetsByKey } from '@data/stylePresets';
import { Link } from 'react-router-dom';
import { getImageUrl } from '@utils/imageUrl';
import { getPlans } from '@services/plans';
import { getOfferCountsForPlans } from '@services/offers';
import { useAuth } from '@services/auth';
import { getMyOffersPermission } from '@services/companies';
import { COMPONENT_LABELS, getComponentLabel } from '@data/componentLabels';
import { useI18n } from '../../i18n';
import { mapApiPlanToPublicPlan } from '@utils/mapApiPlanToPublicPlan';

export default function PublicPlans() {
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const getLocalizedStyleLabel = (styleKey?: string) => {
		switch (styleKey) {
			case 'modern': return copy('Modern', 'Modern', 'Moderni');
			case 'mediterranean': return copy('Mediterranean', 'Mediterran', 'Mediteranski');
			case 'minimalist': return copy('Minimalist', 'Minimalistisch', 'Minimalistički');
			case 'cottage': return copy('Cottage', 'Landhaus', 'Vikendica');
			case 'japanese': return copy('Japanese Zen', 'Japanischer Zen', 'Japanski Zen');
			case 'tropical': return copy('Tropical', 'Tropisch', 'Tropski');
			case 'rustic': return copy('Rustic', 'Rustikal', 'Rustikalni');
			case 'coastal': return copy('Coastal', 'Küstenstil', 'Obalni');
			case 'scandinavian': return copy('Scandinavian', 'Skandinavisch', 'Skandinavski');
			case 'traditional': return copy('Traditional', 'Traditionell', 'Tradicionalni');
			default: return stylePresetsByKey[styleKey as keyof typeof stylePresetsByKey]?.title ?? String(styleKey || '');
		}
	};
	const getLocalizedComponentLabel = (componentId: string) => {
		switch (componentId) {
			case 'pool': return copy('Pool', 'Pool', 'Bazen');
			case 'fence': return copy('Fence', 'Zaun', 'Ograda');
			case 'bbq': return copy('BBQ', 'Grill', 'Roštilj');
			case 'lounge': return copy('Lounge', 'Lounge', 'Zona za odmor');
			case 'outdoor-kitchen': return copy('Outdoor Kitchen', 'Außenküche', 'Vanjska kuhinja');
			case 'fire-pit': return copy('Fire Pit', 'Feuerstelle', 'Ložište');
			case 'pergola': return copy('Pergola', 'Pergola', 'Pergola');
			case 'deck': return copy('Deck', 'Terrasse', 'Terasa');
			case 'shed': return copy('Shed', 'Schuppen', 'Vrtna kućica');
			case 'playground': return copy('Playground', 'Spielplatz', 'Igralište');
			case 'vegetable-garden': return copy('Vegetable Garden', 'Gemüsegarten', 'Povrtnjak');
			case 'water-feature': return copy('Water Feature', 'Wasserelement', 'Vodeni element');
			case 'lighting': return copy('Lighting', 'Beleuchtung', 'Rasvjeta');
			case 'pathway': return copy('Pathway', 'Weg', 'Staza');
			case 'retaining-wall': return copy('Retaining Wall', 'Stützmauer', 'Potporni zid');
			case 'driveway': return copy('Driveway', 'Einfahrt', 'Prilaz');
			case 'greenhouse': return copy('Greenhouse', 'Gewächshaus', 'Staklenik');
			case 'flower-beds': return copy('Flower Beds', 'Blumenbeete', 'Cvjetne gredice');
			case 'hot-tub': return copy('Hot Tub', 'Whirlpool', 'Masažni bazen');
			case 'pizza-oven': return copy('Pizza Oven', 'Pizzaofen', 'Peć za pizzu');
			case 'compost': return copy('Compost Area', 'Kompostbereich', 'Kompost zona');
			default: return getComponentLabel(componentId);
		}
	};
	const [plans, setPlans] = useState<PublicPlan[]>([]);
	const [offers, setOffers] = useState<Offer[]>(getOffers());
	const [offersByPlan, setOffersByPlan] = useState<Record<string, number>>({});
	const [isLoading, setIsLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStyles, setSelectedStyles] = useState<Array<PublicPlan['styleKey']>>([]);
	const [selectedCities, setSelectedCities] = useState<string[]>([]);
	const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
	const [offersFilter, setOffersFilter] = useState<'any' | 'none' | '1plus' | '5plus'>('any');
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'city' | 'style' | 'distance'>('newest');
	const [locationFilter, setLocationFilter] = useState<string>('');
	const [userCoordinates, setUserCoordinates] = useState<{ lat: number; lon: number } | null>(null);
	const [cityCoordinates, setCityCoordinates] = useState<Map<string, { lat: number; lon: number }>>(new Map());
	const [locationCoordinates, setLocationCoordinates] = useState<{ lat: number; lon: number } | null>(null);
	const [isGeocodingLocation, setIsGeocodingLocation] = useState(false);
	const [isGettingLocation, setIsGettingLocation] = useState(false);
	const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
	const [isGeocodingCities, setIsGeocodingCities] = useState(false);
	const [canSendMarketplaceOffers, setCanSendMarketplaceOffers] = useState<boolean | null>(null);

	useEffect(() => {
		if (!user?.id) {
			setCanSendMarketplaceOffers(null);
			return;
		}
		if (user.role === 'company' || user.role === 'admin') {
			setCanSendMarketplaceOffers(true);
			return;
		}
		if (user.role !== 'gardener') {
			setCanSendMarketplaceOffers(false);
			return;
		}
		setCanSendMarketplaceOffers(null);
		getMyOffersPermission(user.id)
			.then((res) => setCanSendMarketplaceOffers(res.can_send_marketplace_offers))
			.catch(() => setCanSendMarketplaceOffers(false));
	}, [user?.id, user?.role]);

	// Fetch marketplace plans from API on mount
	useEffect(() => {
		const fetchPlans = async () => {
			setIsLoading(true);
			try {
				const apiPlans = await getPlans();
				const convertedPlans: PublicPlan[] = apiPlans
					.filter(
						(p) =>
							p.status === 'published' &&
							p.moderation_status !== 'hidden' &&
							p.moderation_status !== 'deleted' &&
							(!p.visibility || p.visibility === 'marketplace') &&
							p.source !== 'invite' &&
							p.exclusive_company_id == null
					)
					.map((apiPlan) => mapApiPlanToPublicPlan(apiPlan));

				const localPlans = getPublicPlans().filter((p) => !p.databaseId);
				setPlans(convertedPlans.length > 0 ? convertedPlans : [...convertedPlans, ...localPlans]);
			} catch (error) {
				console.error('Failed to fetch plans from API:', error);
				setPlans(getPublicPlans());
			} finally {
				setIsLoading(false);
			}
		};

		fetchPlans();

		const handlePlansUpdate = () => {
			fetchPlans();
		};
		const handleOffersUpdate = () => {
			setOffers(getOffers());
		};

		window.addEventListener('publicPlansUpdated', handlePlansUpdate);
		window.addEventListener('offersUpdated', handleOffersUpdate);

		return () => {
			window.removeEventListener('publicPlansUpdated', handlePlansUpdate);
			window.removeEventListener('offersUpdated', handleOffersUpdate);
		};
	}, []);

	const activePlans = useMemo(
		() => plans.filter((p) => p.status === 'active' && p.moderationStatus !== 'hidden'),
		[plans]
	);

	const hasListFilters = useMemo(
		() =>
			Boolean(
				searchTerm.trim() ||
					selectedStyles.length > 0 ||
					selectedCities.length > 0 ||
					selectedComponents.length > 0 ||
					offersFilter !== 'any' ||
					locationFilter.trim()
			),
		[searchTerm, selectedStyles, selectedCities, selectedComponents, offersFilter, locationFilter]
	);

	// Refresh offer counts from API for DB plans (fallback: local offers map)
	useEffect(() => {
		let cancelled = false;

		const refreshOfferCounts = async () => {
			const localCounts = offers.reduce((acc, offer) => {
				if (!acc[offer.planId]) acc[offer.planId] = 0;
				acc[offer.planId]++;
				return acc;
			}, {} as Record<string, number>);

			const nextCounts: Record<string, number> = {};
			for (const plan of activePlans) {
				nextCounts[plan.id] = localCounts[plan.id] ?? 0;
			}

			const plansWithDbId = activePlans
				.map((plan) => {
					const idFromDatabase = Number(plan.databaseId);
					const idFromString = Number(String(plan.id).replace(/^db-/, ''));
					const numericId = Number.isInteger(idFromDatabase) && idFromDatabase > 0
						? idFromDatabase
						: Number.isInteger(idFromString) && idFromString > 0
							? idFromString
							: null;
					return numericId ? { planId: plan.id, dbId: numericId } : null;
				})
				.filter((item): item is { planId: string; dbId: number } => item !== null);

			if (plansWithDbId.length > 0) {
				try {
					const apiCounts = await getOfferCountsForPlans(plansWithDbId.map((item) => item.dbId));
					if (cancelled) return;
					const dbIdToPlanId = new Map(plansWithDbId.map((item) => [item.dbId, item.planId]));
					for (const row of apiCounts) {
						const planId = dbIdToPlanId.get(Number(row.plan_id));
						if (planId) {
							nextCounts[planId] = Number(row.offer_count) || 0;
						}
					}
				} catch {
					// Keep local fallback counts if the API count query fails.
				}
			}

			setOffersByPlan(nextCounts);
		};

		refreshOfferCounts();

		const handleOffersRefresh = () => {
			setOffers(getOffers());
			refreshOfferCounts();
		};
		const handleWindowFocus = () => {
			refreshOfferCounts();
		};
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				refreshOfferCounts();
			}
		};

		window.addEventListener('offersUpdated', handleOffersRefresh);
		window.addEventListener('focus', handleWindowFocus);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			cancelled = true;
			window.removeEventListener('offersUpdated', handleOffersRefresh);
			window.removeEventListener('focus', handleWindowFocus);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [activePlans, offers]);

	// Calculate distance between two coordinates using Haversine formula
	const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
		const R = 6371; // Earth's radius in kilometers
		const dLat = (lat2 - lat1) * Math.PI / 180;
		const dLon = (lon2 - lon1) * Math.PI / 180;
		const a = 
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
			Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c; // Distance in kilometers
	};

	const filteredPlans = useMemo(() => {
		const normalizedSearch = searchTerm.trim().toLowerCase();

		let filtered = activePlans.filter((plan) => {
			const matchesStyle = selectedStyles.length === 0 || selectedStyles.includes(plan.styleKey);

			const matchesCity =
				selectedCities.length === 0 ||
				selectedCities.includes(plan.city);
			const planComponents = plan.questionnaire?.selectedComponents ?? [];
			const matchesComponents =
				selectedComponents.length === 0 ||
				selectedComponents.some((component) => planComponents.includes(component));

			const offerCount = offersByPlan[plan.id] ?? 0;
			const matchesOffers =
				offersFilter === 'any' ||
				(offersFilter === 'none' ? offerCount === 0 :
				 offersFilter === '1plus' ? offerCount >= 1 : offerCount >= 5);

			if (!normalizedSearch) {
				return matchesStyle && matchesCity && matchesComponents && matchesOffers;
			}

			const styleTitle = stylePresetsByKey[plan.styleKey]?.title ?? '';

			const matchesSearch =
				plan.title.toLowerCase().includes(normalizedSearch) ||
				plan.city.toLowerCase().includes(normalizedSearch) ||
				styleTitle.toLowerCase().includes(normalizedSearch);

			return matchesStyle && matchesCity && matchesComponents && matchesOffers && matchesSearch;
		});

		// Apply sorting
		const sorted = [...filtered].sort((a, b) => {
			switch (sortBy) {
				case 'newest':
					return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
				case 'oldest':
					return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
				case 'city':
					return a.city.localeCompare(b.city);
				case 'style':
					const styleA = stylePresetsByKey[a.styleKey]?.title ?? '';
					const styleB = stylePresetsByKey[b.styleKey]?.title ?? '';
					return styleA.localeCompare(styleB);
				case 'distance':
					// Sort by actual distance from location (user's GPS or manually entered location)
					const referenceCoords = locationCoordinates || userCoordinates;
					
					if (referenceCoords) {
						const aCoords = cityCoordinates.get(a.city);
						const bCoords = cityCoordinates.get(b.city);
						
						// If we have coordinates for both, calculate distances
						if (aCoords && bCoords) {
							const distA = calculateDistance(
								referenceCoords.lat, referenceCoords.lon,
								aCoords.lat, aCoords.lon
							);
							const distB = calculateDistance(
								referenceCoords.lat, referenceCoords.lon,
								bCoords.lat, bCoords.lon
							);
							return distA - distB;
						}
						
						// If only one has coordinates, prioritize it
						if (aCoords && !bCoords) return -1;
						if (!aCoords && bCoords) return 1;
						
						// If neither has coordinates yet, sort alphabetically (will update when geocoded)
						return a.city.localeCompare(b.city);
					}
					
					// Fallback: if no coordinates but location filter set, use text matching
					if (locationFilter.trim()) {
						const locationLower = locationFilter.trim().toLowerCase();
						const aMatches = a.city.toLowerCase().includes(locationLower) || locationLower.includes(a.city.toLowerCase());
						const bMatches = b.city.toLowerCase().includes(locationLower) || locationLower.includes(b.city.toLowerCase());
						
						if (aMatches && !bMatches) return -1;
						if (!aMatches && bMatches) return 1;
					}
					return a.city.localeCompare(b.city);
				default:
					return 0;
			}
		});

		return sorted;
	}, [activePlans, searchTerm, selectedStyles, selectedCities, selectedComponents, offersFilter, offersByPlan, sortBy, locationFilter, userCoordinates, locationCoordinates, cityCoordinates]);

	const styleOptions = useMemo(() => Object.entries(stylePresetsByKey), []);
	const cityOptions = useMemo(
		() => Array.from(new Set(activePlans.map((plan) => plan.city))).sort(),
		[activePlans]
	);
	const componentOptions = useMemo(
		() => Object.entries(COMPONENT_LABELS).map(([value]) => ({ value, label: getLocalizedComponentLabel(value) })),
		[language]
	);

	const activeFiltersCount = useMemo(() => {
		let count = 0;
		count += selectedStyles.length;
		count += selectedCities.length;
		count += selectedComponents.length;
		if (offersFilter !== 'any') count += 1;
		return count;
	}, [selectedStyles, selectedCities, selectedComponents, offersFilter]);

	const toggleSelection = <T,>(value: T, collection: T[], setCollection: (items: T[]) => void) => {
		setCollection(
			collection.includes(value)
				? collection.filter((item) => item !== value)
				: [...collection, value]
		);
	};

	const handleResetFilters = () => {
		setSelectedStyles([]);
		setSelectedCities([]);
		setSelectedComponents([]);
		setOffersFilter('any');
	};

	const uniqueCities = useMemo(() => new Set(filteredPlans.map(p => p.city)).size, [filteredPlans]);
	
	// Geocode a location (city name or full address) to get coordinates
	const geocodeLocation = async (location: string): Promise<{ lat: number; lon: number } | null> => {
		if (!location.trim()) return null;

		try {
			const response = await fetch(
				`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&limit=1&addressdetails=1`,
				{
					headers: {
						'User-Agent': 'HortEra Marketplace'
					}
				}
			);
			const data = await response.json();
			
			if (data && data.length > 0) {
				return {
					lat: parseFloat(data[0].lat),
					lon: parseFloat(data[0].lon)
				};
			}
		} catch (error) {
			console.error(`Failed to geocode location ${location}:`, error);
		}
		return null;
	};

	// Geocode a city name to get coordinates (for plan cities)
	const geocodeCity = async (cityName: string): Promise<{ lat: number; lon: number } | null> => {
		if (cityCoordinates.has(cityName)) {
			return cityCoordinates.get(cityName)!;
		}

		const coords = await geocodeLocation(cityName);
		if (coords) {
			setCityCoordinates(prev => new Map(prev).set(cityName, coords));
		}
		return coords;
	};

	// Function to get and set user's location
	const handleUseMyLocation = () => {
		if (!navigator.geolocation) {
			// Geolocation not supported - user can enter location manually
			return;
		}

		setIsGettingLocation(true);
		// Request location - this will trigger the browser's permission prompt if needed
		// Calling getCurrentPosition is what triggers the permission prompt
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				try {
					// Save user coordinates
					const { latitude, longitude } = position.coords;
					setUserCoordinates({ lat: latitude, lon: longitude });
					
					// Reverse geocode coordinates to get city name
					const response = await fetch(
						`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
						{
							headers: {
								'User-Agent': 'HortEra Marketplace'
							}
						}
					);
					const data = await response.json();
					
					// Extract city name from address
					const city = data.address?.city || 
						data.address?.town || 
						data.address?.village || 
						data.address?.municipality ||
						data.address?.county ||
						data.address?.state ||
						'';
					
					if (city) {
						setLocationFilter(city);
						setLocationCoordinates({ lat: latitude, lon: longitude });
						// Don't auto-switch to distance - let user choose
					}
				} catch (error) {
					// Failed to reverse geocode - user can enter location manually
					// Silently fail - no need to show error
				} finally {
					setIsGettingLocation(false);
				}
			},
			(error) => {
				// Permission denied or other error - user can enter location manually
				// Don't show alerts to user, they can enter location manually
				if (error.code === error.PERMISSION_DENIED) {
					// Permission was denied - this is expected and OK
				} else if (error.code === error.POSITION_UNAVAILABLE) {
					// Position unavailable - GPS might be off
				} else if (error.code === error.TIMEOUT) {
					// Timeout - location request took too long
				}
				// Silently allow user to enter location manually
				setIsGettingLocation(false);
			},
			{
				enableHighAccuracy: false, // Use lower accuracy for faster response (good enough for city-level)
				timeout: 5000, // Reduced timeout for faster failure/fallback
				maximumAge: 300000 // Allow cached location up to 5 minutes old (faster if available)
			}
		);
	};

	// Check permission state on mount
	useEffect(() => {
		if ('permissions' in navigator && navigator.permissions) {
			(navigator.permissions as any).query({ name: 'geolocation' }).then((result: PermissionStatus) => {
				setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
				
				// If permission is already granted, get location automatically
				if (result.state === 'granted' && !locationFilter && !userCoordinates) {
					handleUseMyLocation();
				}
				
				// Listen for permission changes
				result.addEventListener('change', () => {
					const newState = result.state as 'prompt' | 'granted' | 'denied';
					setPermissionState(newState);
					// When permission is granted, try to get location
					if (newState === 'granted') {
						// Small delay to ensure permission is fully processed
						setTimeout(() => {
							handleUseMyLocation();
						}, 100);
					}
				});
			}).catch(() => {
				// Permissions API not fully supported, set to unknown
				setPermissionState('unknown');
				// Try to request anyway
				if (navigator.geolocation && !locationFilter && !userCoordinates) {
					handleUseMyLocation();
				}
			});
		} else {
			// Permissions API not supported, try to request anyway
			setPermissionState('unknown');
			if (navigator.geolocation && !locationFilter && !userCoordinates) {
				handleUseMyLocation();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Geocode the manually entered location when it changes
	useEffect(() => {
		const timeoutId = setTimeout(async () => {
			if (locationFilter.trim() && sortBy === 'distance') {
				// Don't geocode if it's the same as user's detected location
				if (userCoordinates && locationCoordinates) {
					return;
				}
				
				setIsGeocodingLocation(true);
				const coords = await geocodeLocation(locationFilter.trim());
				if (coords) {
					setLocationCoordinates(coords);
				} else {
					setLocationCoordinates(null);
				}
				setIsGeocodingLocation(false);
			} else if (!locationFilter.trim()) {
				setLocationCoordinates(null);
			}
		}, 800); // Debounce: wait 800ms after user stops typing

		return () => clearTimeout(timeoutId);
	}, [locationFilter, sortBy, userCoordinates, locationCoordinates]);

	// Geocode all unique cities when sorting by distance (batch geocoding to prevent one-by-one updates)
	useEffect(() => {
		if (sortBy === 'distance' && (userCoordinates || locationCoordinates)) {
			const uniqueCities = Array.from(new Set(activePlans.map(p => p.city)));
			const citiesToGeocode = uniqueCities.filter(city => !cityCoordinates.has(city));
			
			if (citiesToGeocode.length > 0) {
				setIsGeocodingCities(true);
				// Batch geocode all cities at once, then update state in one go
				Promise.all(
					citiesToGeocode.map(city => 
						geocodeLocation(city).then(coords => ({ city, coords }))
					)
				).then(results => {
					// Update all coordinates at once to prevent individual re-renders
					setCityCoordinates(prev => {
						const newMap = new Map(prev);
						results.forEach(({ city, coords }) => {
							if (coords) {
								newMap.set(city, coords);
							}
						});
						return newMap;
					});
					setIsGeocodingCities(false);
				}).catch(err => {
					console.error('Geocoding error:', err);
					setIsGeocodingCities(false);
				});
			} else {
				setIsGeocodingCities(false);
			}
		} else {
			setIsGeocodingCities(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortBy, userCoordinates, locationCoordinates, activePlans]);
	
	// Check if plan is "new" (added within last 7 days)
	const isNewPlan = (plan: PublicPlan) => {
		const daysSinceCreation = (Date.now() - new Date(plan.createdAt).getTime()) / (1000 * 60 * 60 * 24);
		return daysSinceCreation <= 7;
	};

	const isBusy = isLoading;

	return (
		<>
			{/* Marketplace Hero Section */}
			<div className="marketplace-hero mb-4 rounded-4 p-4 p-lg-5 position-relative overflow-hidden">
				<div className="position-absolute top-0 start-0 w-100 h-100 marketplace-hero-bg"></div>
				<div className="position-relative">
					<div className="row align-items-center g-4">
						<div className="col-12 col-lg-8">
							<div className="d-flex align-items-center gap-3 mb-3">
								<div className="marketplace-icon">
									<i className="bi bi-shop"></i>
								</div>
								<div>
									<h1 className="h2 mb-1 fw-bold text-success-emphasis">{copy('Marketplace', 'Marktplatz', 'Marketplace')}</h1>
									<p className="text-muted mb-0">
										{copy(
											'Discover garden projects and connect with opportunities',
											'Entdecken Sie Gartenprojekte und finden Sie neue Möglichkeiten',
											'Otkrijte vrtne projekte i povežite se s prilikama'
										)}
									</p>
									{(user?.role === 'company' || user?.role === 'admin') && (
										<p className="small mt-2 mb-0">
											<Link to="/app/direct-plans" className="text-success text-decoration-none fw-semibold">
												<i className="bi bi-person-lines-fill me-1" />
												{copy('Direct plans', 'Direkte Pläne', 'Izravni planovi')}
											</Link>
											<span className="text-muted">
												{' '}
												—{' '}
												{copy(
													'plans for your company (drafts, invites, clients)',
													'Pläne für Ihr Unternehmen (Entwürfe, Einladungen, Kunden)',
													'planovi za vašu tvrtku (nacrti, pozivnice, klijenti)'
												)}
											</span>
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="col-12 col-lg-4">
							<div className="marketplace-stat-card p-3 rounded-3">
								<div className="d-flex align-items-center gap-2 mb-1">
									<i className="bi bi-collection text-success"></i>
									<span className="text-muted small">{copy('Active Plans', 'Aktive Pläne', 'Aktivni planovi')}</span>
								</div>
								<div className="h4 mb-0 fw-bold text-success-emphasis">{activePlans.length}</div>
								{hasListFilters && filteredPlans.length !== activePlans.length && (
									<div className="text-muted small mt-1">
										{filteredPlans.length} {copy('shown with filters', 'mit Filtern', 's filtrima')}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Search and Filters */}
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
				<div className="flex-grow-1" style={{ maxWidth: '500px' }}>
					<div className="input-group">
						<span className="input-group-text bg-white border-end-0">
							<i className="bi bi-search text-muted"></i>
						</span>
						<input
							type="search"
							className="form-control border-start-0"
							placeholder={copy('Search by title, city, or style...', 'Suche nach Titel, Stadt oder Stil...', 'Pretraži po naslovu, gradu ili stilu...')}
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
							style={{ boxShadow: 'none' }}
						/>
					</div>
				</div>
				<div className="d-flex gap-2 align-items-center flex-wrap">
					<div className="d-flex align-items-center gap-2">
						<label className="text-muted small mb-0">{copy('Location:', 'Standort:', 'Lokacija:')}</label>
						<div className="input-group" style={{ width: 'auto' }}>
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder={copy('City or address...', 'Stadt oder Adresse...', 'Grad ili adresa...')}
								value={locationFilter}
							onChange={(e) => {
								setLocationFilter(e.target.value);
								// Clear location coordinates when user types (will be re-geocoded)
								if (e.target.value.trim() !== locationFilter) {
									setLocationCoordinates(null);
								}
							}}
								style={{ width: '180px' }}
							/>
							{(isGeocodingLocation || isGettingLocation) && (
								<span className="input-group-text bg-white">
									<span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true"></span>
								</span>
							)}
							<button
								type="button"
								className="btn btn-outline-success btn-sm"
								onClick={() => {
									// Always try to get location when button is clicked
									handleUseMyLocation();
								}}
								disabled={isGettingLocation}
								title={permissionState === 'denied' ? 'Location permission denied. Click to try again or enter manually.' : 'Use my current location'}
							>
								<i className="bi bi-geo-alt-fill"></i>
							</button>
							{permissionState === 'prompt' && !locationFilter && (
								<small className="text-muted ms-2 d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
									<i className="bi bi-info-circle"></i>
									Click the location icon to enable automatic location detection
								</small>
							)}
						</div>
					</div>
					<div className="d-flex align-items-center gap-2">
						<label className="text-muted small mb-0">{copy('Sort:', 'Sortieren:', 'Sortiraj:')}</label>
						<select
							className="form-select form-select-sm"
							style={{ width: 'auto', minWidth: '120px' }}
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
						>
							<option value="newest">{copy('Newest First', 'Neueste zuerst', 'Najnovije prvo')}</option>
							<option value="oldest">{copy('Oldest First', 'Älteste zuerst', 'Najstarije prvo')}</option>
							<option value="distance">{copy('Distance', 'Entfernung', 'Udaljenost')}</option>
							<option value="city">{copy('By City', 'Nach Stadt', 'Po gradu')}</option>
							<option value="style">{copy('By Style', 'Nach Stil', 'Po stilu')}</option>
						</select>
					</div>
					<FilterToggle
						isOpen={isFiltersOpen}
						onToggle={() => setIsFiltersOpen((prev) => !prev)}
						activeCount={activeFiltersCount}
					/>
				</div>
			</div>

			{/* Active Filter Chips */}
			{activeFiltersCount > 0 && (
				<div className="d-flex flex-wrap align-items-center gap-2 mb-4">
				{selectedStyles.length > 0 && selectedStyles.map((styleKey) => (
					<span
						key={styleKey}
						className="badge text-bg-light text-dark d-flex align-items-center gap-1"
						style={{ cursor: 'pointer', fontSize: '0.875rem' }}
						onClick={() => setSelectedStyles(selectedStyles.filter(s => s !== styleKey))}
					>
						{getLocalizedStyleLabel(styleKey)}
						<i className="bi bi-x"></i>
					</span>
				))}
				{selectedCities.length > 0 && selectedCities.map((city) => (
					<span
						key={city}
						className="badge text-bg-light text-dark d-flex align-items-center gap-1"
						style={{ cursor: 'pointer', fontSize: '0.875rem' }}
						onClick={() => setSelectedCities(selectedCities.filter(c => c !== city))}
					>
						<i className="bi bi-geo-alt-fill"></i>
						{city}
						<i className="bi bi-x"></i>
					</span>
				))}
				{selectedComponents.length > 0 && selectedComponents.map((componentId) => (
					<span
						key={componentId}
						className="badge text-bg-light text-dark d-flex align-items-center gap-1"
						style={{ cursor: 'pointer', fontSize: '0.875rem' }}
						onClick={() => setSelectedComponents(selectedComponents.filter(c => c !== componentId))}
					>
						<i className="bi bi-grid-1x2-fill"></i>
						{getLocalizedComponentLabel(componentId)}
						<i className="bi bi-x"></i>
					</span>
				))}
				{offersFilter !== 'any' && (
					<span
						className="badge text-bg-light text-dark d-flex align-items-center gap-1"
						style={{ cursor: 'pointer', fontSize: '0.875rem' }}
						onClick={() => setOffersFilter('any')}
					>
						{offersFilter === 'none' ? copy('No offers yet', 'Noch keine Angebote', 'Još nema ponuda') : offersFilter === '1plus' ? copy('Has offers', 'Hat Angebote', 'Ima ponude') : copy('5+ offers', '5+ Angebote', '5+ ponuda')}
						<i className="bi bi-x"></i>
					</span>
				)}
					<button
						type="button"
						className="btn btn-sm btn-link text-muted p-0"
						onClick={handleResetFilters}
						style={{ fontSize: '0.875rem' }}
					>
						{copy('Clear all', 'Alles löschen', 'Očisti sve')}
					</button>
				</div>
			)}
			<FiltersPanel
				isOpen={isFiltersOpen}
				onApply={() => setIsFiltersOpen(false)}
				onReset={handleResetFilters}
			>
				<div className="row g-4">
					<div className="col-12 col-lg-4">
						<SearchableMultiSelect
							id="marketplace-style-filter"
							label={copy('Style', 'Stil', 'Stil')}
							placeholder={copy('Select style(s)...', 'Stil(e) auswählen...', 'Odaberite stil(ove)...')}
							options={styleOptions.map(([key]) => ({ value: key, label: getLocalizedStyleLabel(key) }))}
							selectedValues={selectedStyles}
							onToggle={(value) =>
								toggleSelection(
									value as PublicPlan['styleKey'],
									selectedStyles,
									setSelectedStyles
								)
							}
							emptyText={copy('No style found.', 'Kein Stil gefunden.', 'Nije pronađen stil.')}
						/>
					</div>
					<div className="col-12 col-lg-4">
						<SearchableMultiSelect
							id="marketplace-city-filter"
							label={copy('City', 'Stadt', 'Grad')}
							placeholder={copy('Select city/cities...', 'Stadt/Städte auswählen...', 'Odaberite grad(ove)...')}
							options={cityOptions.map((city) => ({ value: city, label: city }))}
							selectedValues={selectedCities}
							onToggle={(value) => toggleSelection(value, selectedCities, setSelectedCities)}
							emptyText={copy('Cities appear once plans are available.', 'Städte erscheinen, sobald Pläne verfügbar sind.', 'Gradovi se pojavljuju kada planovi budu dostupni.')}
						/>
					</div>
					<div className="col-12 col-lg-4">
						<SearchableMultiSelect
							id="marketplace-component-filter"
							label={copy('Components', 'Komponenten', 'Komponente')}
							placeholder={copy('Select component(s)...', 'Komponente(n) auswählen...', 'Odaberite komponentu(e)...')}
							options={componentOptions}
							selectedValues={selectedComponents}
							onToggle={(value) => toggleSelection(value, selectedComponents, setSelectedComponents)}
							emptyText={copy('No components found.', 'Keine Komponenten gefunden.', 'Nema pronađenih komponenti.')}
						/>
					</div>
					<div className="col-12">
						<div className="mb-2 text-muted text-uppercase small">{copy('Offers', 'Angebote', 'Ponude')}</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								name="public-plan-offers-filter"
								id="public-plan-offers-any"
								checked={offersFilter === 'any'}
								onChange={() => setOffersFilter('any')}
							/>
							<label className="form-check-label" htmlFor="public-plan-offers-any">
								{copy('Any', 'Beliebig', 'Bilo koji')}
							</label>
						</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								name="public-plan-offers-filter"
								id="public-plan-offers-none"
								checked={offersFilter === 'none'}
								onChange={() => setOffersFilter('none')}
							/>
							<label className="form-check-label" htmlFor="public-plan-offers-none">
								{copy('No offers yet', 'Noch keine Angebote', 'Još nema ponuda')}
							</label>
						</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								name="public-plan-offers-filter"
								id="public-plan-offers-1plus"
								checked={offersFilter === '1plus'}
								onChange={() => setOffersFilter('1plus')}
							/>
							<label className="form-check-label" htmlFor="public-plan-offers-1plus">
								{copy('Has at least 1 offer', 'Hat mindestens 1 Angebot', 'Ima barem 1 ponudu')}
							</label>
						</div>
						<div className="form-check">
							<input
								className="form-check-input"
								type="radio"
								name="public-plan-offers-filter"
								id="public-plan-offers-5plus"
								checked={offersFilter === '5plus'}
								onChange={() => setOffersFilter('5plus')}
							/>
							<label className="form-check-label" htmlFor="public-plan-offers-5plus">
								{copy('5+ offers', '5+ Angebote', '5+ ponuda')}
							</label>
						</div>
					</div>
				</div>
			</FiltersPanel>
			{isBusy ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<div className="spinner-border text-success mb-3" role="status">
							<span className="visually-hidden">{copy('Loading...', 'Laden...', 'Učitavanje...')}</span>
						</div>
						<p className="text-muted mb-0">{copy('Loading opportunities...', 'Möglichkeiten werden geladen...', 'Učitavanje prilika...')}</p>
					</div>
				</div>
			) : filteredPlans.length === 0 ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<div className="mb-3">
							<i className="bi bi-inbox display-4 text-muted"></i>
						</div>
						<p className="text-muted mb-0">
							{activePlans.length === 0
								? copy(
									'No active opportunities in the marketplace at the moment. Check back soon!',
									'Derzeit gibt es keine aktiven Möglichkeiten im Marktplatz. Schauen Sie bald wieder vorbei!',
									'Trenutno nema aktivnih prilika na tržnici. Provjerite uskoro ponovno!'
								)
								: copy(
									'No opportunities match your filters. Try adjusting your search criteria.',
									'Keine Möglichkeiten entsprechen Ihren Filtern. Passen Sie Ihre Suchkriterien an.',
									'Nijedna prilika ne odgovara vašim filtrima. Pokušajte prilagoditi kriterije pretraživanja.'
								)}
						</p>
					</div>
				</div>
			) : (
				<>
					<div className="d-flex align-items-center justify-content-between mb-3">
						<div>
							<h3 className="h5 mb-1 fw-semibold text-success-emphasis">
								{filteredPlans.length}{' '}
								{filteredPlans.length === 1
									? copy('Opportunity', 'Möglichkeit', 'Prilika')
									: copy('Opportunities', 'Möglichkeiten', 'Prilike')}{' '}
								{copy('Available', 'Verfügbar', 'Dostupno')}
							</h3>
							<p className="text-muted small mb-0">
								{uniqueCities > 0 && `${uniqueCities} ${uniqueCities === 1 ? copy('city', 'Stadt', 'grad') : copy('cities', 'Städte', 'gradova')} • `}
								{user?.role === 'gardener' && canSendMarketplaceOffers !== true
									? copy(
											'Browse published plans; open one to view full details. Your company must enable marketplace offers for you in Team settings.',
											'Durchsuchen Sie veröffentlichte Pläne; öffnen Sie einen für alle Details. Ihr Unternehmen muss Ihnen Marktplatz-Angebote im Team freischalten.',
											'Pregledajte objavljene planove; otvorite plan za pune detalje. Vaša tvrtka mora vam omogućiti slanje ponuda u postavkama Tima.'
										)
									: copy('Browse and respond to garden projects', 'Durchsuchen und beantworten Sie Gartenprojekte', 'Pregledajte i odgovorite na vrtne projekte')}
							</p>
						</div>
					</div>
					<div className="position-relative">
						<div className="row g-4">
						{filteredPlans.map(p => {
							// Get the image URL - check AI generated photo first, but skip if it's a placeholder
							// Then use before photo, then fallback to default
							const afterImageUrl = p.aiGenerated?.photoAfterUrl;
							const isPlaceholder = afterImageUrl?.includes('unsplash.com');
							const planImage = (afterImageUrl && !isPlaceholder) 
								? afterImageUrl 
								: p.photoBeforeUrl || 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop';
							const imageUrl = getImageUrl(planImage) || planImage; // Fallback to planImage if getImageUrl returns undefined
							
							const planHref =
								user?.role === 'company' || user?.role === 'admin' || (user?.role === 'gardener' && canSendMarketplaceOffers === true)
									? `/app/plans/${p.id}`
									: `/app/seeker/plan/${p.id}`;
							return (
							<div key={p.id} className="col-12 col-md-6 col-lg-4">
								<Link 
									to={planHref} 
									className="text-decoration-none"
									style={{ display: 'block', height: '100%' }}
								>
									<div className={`marketplace-card card h-100 border-0 shadow-sm position-relative overflow-hidden ${(offersByPlan[p.id] ?? 0) === 0 ? 'border-warning border-2' : ''}`} style={(offersByPlan[p.id] ?? 0) === 0 ? { borderStyle: 'solid' } : {}}>
										<div className="marketplace-card-image position-relative">
											<img 
												src={imageUrl} 
												className="card-img-top" 
												alt={p.title}
												style={{ height: '220px', objectFit: 'cover', width: '100%' }}
											/>
											{(offersByPlan[p.id] ?? 0) === 0 && (
												<div className="position-absolute bottom-0 start-0 w-100 bg-warning text-dark text-center py-2 px-2 shadow-sm" style={{ 
													zIndex: 10,
													fontSize: '0.8rem',
													fontWeight: '600',
													letterSpacing: '0.5px',
													lineHeight: '1.2'
												}}>
													<i className="bi bi-envelope-fill me-1"></i>
													{copy('Be the first! No offers yet', 'Seien Sie der Erste! Noch keine Angebote', 'Budite prvi! Još nema ponuda')}
												</div>
											)}
											<div className="position-absolute top-0 end-0 m-2 d-flex flex-column gap-1" style={{ alignItems: 'flex-end' }}>
												{isNewPlan(p) && (
													<span className="badge text-bg-warning shadow-sm">
														<i className="bi bi-star-fill me-1"></i>
														{copy('New', 'Neu', 'Novo')}
													</span>
												)}
											</div>
											{p.aiGenerated && (
												<div className="position-absolute top-0 start-0 m-2">
													<span className="badge text-bg-primary shadow-sm">
														<i className="bi bi-magic me-1"></i>
														{copy('Design available', 'Design verfügbar', 'Dizajn dostupan')}
													</span>
												</div>
											)}
										</div>
										<div className="card-body d-flex flex-column">
											<div className="mb-3">
												<h4 className="h5 fw-bold text-success-emphasis mb-2">{p.title}</h4>
												<div className="d-flex align-items-center gap-2 text-muted small mb-2">
													<i className="bi bi-geo-alt-fill"></i>
													<span>{p.city}</span>
													<span>•</span>
													<i className="bi bi-palette-fill"></i>
													<span>{getLocalizedStyleLabel(p.styleKey)}</span>
												</div>
												{p.questionnaire?.selectedComponents && p.questionnaire.selectedComponents.length > 0 && (
													<div className="d-flex flex-wrap gap-2 mb-2">
														{p.questionnaire.selectedComponents.slice(0, 4).map((componentId) => (
															<span key={componentId} className="badge bg-success-subtle text-success-emphasis border border-success-subtle">
																<i className="bi bi-grid-1x2-fill me-1"></i>
																{getLocalizedComponentLabel(componentId)}
															</span>
														))}
														{p.questionnaire.selectedComponents.length > 4 && (
															<span className="badge bg-light text-muted border">
																+{p.questionnaire.selectedComponents.length - 4}
															</span>
														)}
													</div>
												)}
												<div className="d-flex align-items-center gap-2 text-muted small mb-2">
													<i className="bi bi-calendar3"></i>
													<span>{copy('Posted', 'Veröffentlicht', 'Objavljeno')} {new Date(p.createdAt).toLocaleDateString(locale)}</span>
													{(offersByPlan[p.id] ?? 0) === 0 ? (
														<>
															<span>•</span>
															<span className="text-warning-emphasis fw-semibold">
																<i className="bi bi-envelope-fill me-1"></i>
																{copy('0 offers - Be the first!', '0 Angebote - Seien Sie der Erste!', '0 ponuda - Budite prvi!')}
															</span>
														</>
													) : (
														<>
															<span>•</span>
															<i className="bi bi-envelope-fill"></i>
															<span>{offersByPlan[p.id]} {offersByPlan[p.id] === 1 ? copy('offer', 'Angebot', 'ponuda') : copy('offers', 'Angebote', 'ponude')}</span>
														</>
													)}
												</div>
												{/* Structured summary: budget and/or timeline when available */}
												{(p.questionnaire?.budgetRange || p.questionnaire?.timeline) && (
													<p className="small mt-2 mb-0 text-muted d-flex flex-wrap align-items-center gap-2">
														{p.questionnaire.budgetRange && (
															<span>
																<i className="bi bi-currency-euro me-1"></i>
																{p.questionnaire.budgetRange.min?.toLocaleString()}–{p.questionnaire.budgetRange.max?.toLocaleString()} EUR
															</span>
														)}
														{p.questionnaire.budgetRange && p.questionnaire.timeline && <span>•</span>}
														{p.questionnaire.timeline && (
															<span>
																<i className="bi bi-clock me-1"></i>
																{p.questionnaire.timeline}
															</span>
														)}
													</p>
												)}
											</div>
											<div className="mt-auto pt-3 border-top">
												<div className="d-flex">
													<div className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2">
													<i className="bi bi-eye"></i>
													{copy('View Opportunity', 'Möglichkeit ansehen', 'Pogledaj priliku')}
													</div>
												</div>
											</div>
										</div>
									</div>
								</Link>
							</div>
							);
						})}
						</div>
						{/* Loading overlay when sorting by distance and geocoding is in progress */}
						{sortBy === 'distance' && isGeocodingCities && (
							<div className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-start justify-content-center" style={{
								backgroundColor: 'rgba(255, 255, 255, 0.9)',
								zIndex: 10,
								borderRadius: '0.5rem',
								backdropFilter: 'blur(2px)',
								paddingTop: '2rem'
							}}>
								<div className="text-center p-4">
									<div className="spinner-border text-success mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
										<span className="visually-hidden">Loading...</span>
									</div>
									<p className="text-muted mb-0 fw-semibold">Calculating distances...</p>
									<p className="text-muted small mt-1">Please wait while we sort plans by distance</p>
								</div>
							</div>
						)}
					</div>
				</>
			)}
		</>
	);
}


